import React, { useEffect } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import 'chart.js/auto'; // This imports all controllers
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Badge, Box, Flex, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { FaBuilding, FaHome } from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
);

// CSS for the loading animation
const keyframes = `
  @keyframes pulse {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
`;

function PriceHistoryChart({ data }) {
  // AI analysis is now handled by the parent component

  // Check if data is fully loaded
  const isDataComplete = () => {
    return (
      !!data &&
      !!data.series &&
      !!data.series[0] &&
      !!data.series[0].data &&
      data.series[0].data.length > 0 &&
      !!data.propertyAddress &&
      !!data.postcode
    );
  };

  // AI analysis is now handled by the parent component
  // isDataComplete function is still useful for other validations
  useEffect(() => {
    if (isDataComplete()) {
      console.log('House price data fully loaded');
    } else {
      console.log('Waiting for complete house price data...');
    }
  }, [data]);

  if (!data?.series?.[0]?.data?.length) {
    return <div>No price history available</div>;
  }

  // Enhanced colors with better contrast and accessibility
  const colors = {
    Terraced: '#FF5733', // Bright Red
    'Semi-Detached': '#3498DB', // Blue
    Detached: '#2ECC71', // Green
    'Flat/Maisonette': '#9B59B6', // Purple
    Other: '#F39C12', // Orange
    Property: '#3498DB', // Blue for single property
  };

  // Property type icons mapping
  const propertyIcons = {
    Terraced: FaHome,
    'Semi-Detached': FaHome,
    Detached: FaHome,
    'Flat/Maisonette': FaBuilding,
    Other: FaHome,
    Property: FaHome,
  };

  // Map property type codes to full descriptions
  const propertyTypeMap = {
    T: 'Terraced',
    S: 'Semi-Detached',
    D: 'Detached',
    F: 'Flat/Maisonette',
    O: 'Other',
    'Sale Price': 'Property',
    // Handle full property type names (lowercase)
    terraced: 'Terraced',
    'semi-detached': 'Semi-Detached',
    detached: 'Detached',
    flat: 'Flat/Maisonette',
  };

  // Find min and max prices for better scaling
  const allPrices = data.series.flatMap((series) => series.data.map((d) => d.y));
  const minPrice = Math.floor(Math.min(...allPrices) / 100000) * 100000;
  const maxPrice = Math.ceil(Math.max(...allPrices) / 100000) * 100000;

  // Determine if this is a single property or area chart
  const isSingleProperty = data.series.length === 1 && data.series[0].name === 'Sale Price';

  // Function to calculate linear regression for trend analysis
  const calculateTrendline = (points) => {
    const n = points.length;
    if (n < 2) return null;

    // Convert dates to timestamps for calculation
    const xValues = points.map((p) => p.x);
    const yValues = points.map((p) => p.y);

    const xMean = xValues.reduce((a, b) => a + b, 0) / n;
    const yMean = yValues.reduce((a, b) => a + b, 0) / n;

    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < n; i++) {
      numerator += (xValues[i] - xMean) * (yValues[i] - yMean);
      denominator += Math.pow(xValues[i] - xMean, 2);
    }

    const slope = numerator / denominator;
    const intercept = yMean - slope * xMean;

    // Create trendline points
    return [
      { x: Math.min(...xValues), y: slope * Math.min(...xValues) + intercept },
      { x: Math.max(...xValues), y: slope * Math.max(...xValues) + intercept },
    ];
  };

  // Calculate total percentage change for overall trend display
  const calculateOverallTrend = () => {
    if (data.series.length === 0 || !data.series[0].data.length) return null;

    let combined = [];

    // If single property, use that series directly
    if (isSingleProperty) {
      combined = data.series[0].data;
    } else {
      // For area charts, combine all data points and sort by date
      combined = data.series.flatMap((series) => series.data).sort((a, b) => a.x - b.x);
    }

    // Group by year for a cleaner trend calculation
    const yearlyAverages = {};
    combined.forEach((point) => {
      const year = new Date(point.x).getFullYear();
      if (!yearlyAverages[year]) {
        yearlyAverages[year] = { sum: 0, count: 0 };
      }
      yearlyAverages[year].sum += point.y;
      yearlyAverages[year].count += 1;
    });

    // Convert to array of yearly averages
    const yearlyData = Object.keys(yearlyAverages)
      .sort()
      .map((year) => ({
        year: parseInt(year),
        average: yearlyAverages[year].sum / yearlyAverages[year].count,
      }));

    if (yearlyData.length < 2) return null;

    // Calculate percentage change from first to last year
    const firstYearAvg = yearlyData[0].average;
    const lastYearAvg = yearlyData[yearlyData.length - 1].average;

    return {
      percentChange: ((lastYearAvg - firstYearAvg) / firstYearAvg) * 100,
      firstYear: yearlyData[0].year,
      lastYear: yearlyData[yearlyData.length - 1].year,
      firstYearAvg,
      lastYearAvg,
      yearlyData,
    };
  };

  const trendAnalysis = calculateOverallTrend();
  const overallTrend = trendAnalysis?.percentChange;

  // Calculate annualized growth rate if we have enough data
  const calculateAnnualizedRate = () => {
    if (!trendAnalysis) return null;
    const { firstYearAvg, lastYearAvg, firstYear, lastYear } = trendAnalysis;
    const years = lastYear - firstYear;
    if (years < 1) return null;

    // Compound Annual Growth Rate formula
    return (Math.pow(lastYearAvg / firstYearAvg, 1 / years) - 1) * 100;
  };

  const annualizedRate = calculateAnnualizedRate();

  const chartData = {
    datasets: data.series.flatMap((series, index) => {
      // Get the property type using our mapping for consistency
      let propertyType = 'Other';

      if (series.data && series.data.length > 0 && series.data[0].propertyType) {
        // If the data point has a propertyType property, use it
        const rawType = series.data[0].propertyType;
        propertyType =
          propertyTypeMap[rawType] || rawType.charAt(0).toUpperCase() + rawType.slice(1);
      } else if (series.name) {
        // Fall back to the series name
        propertyType = propertyTypeMap[series.name] || series.name;
      }

      const color = colors[propertyType] || `hsl(${index * 60}, 70%, 50%)`;
      const trendline = calculateTrendline(series.data);

      return [
        // Scatter plot for data points
        {
          ...series,
          type: 'line',
          showLine: false,
          label: series.name, // Use the original series name instead of property type
          borderColor: series.borderColor || color,
          backgroundColor: series.backgroundColor || color,
          pointRadius: series.pointRadius || 4,
          pointStyle: 'circle',
          showLine: false, // Ensure no lines between points
        },
        // Line for trendline
        trendline && {
          type: 'line',
          label: `${series.name} Trend`,
          data: trendline,
          borderColor: series.borderColor || color,
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
          borderDash: [5, 5],
          showLine: true,
        },
      ].filter(Boolean);
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
          filter: (legendItem) => !legendItem.text.includes('Trend'),
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const date = new Date(tooltipItems[0].parsed.x);
            return date.toLocaleDateString('en-GB', {
              month: 'long',
              year: 'numeric',
            });
          },
          label: (context) => `${context.dataset.label}: £${context.parsed.y.toLocaleString()}`,
        },
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        titleColor: '#2D3748',
        bodyColor: '#4A5568',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#E2E8F0',
        borderWidth: 1,
        boxPadding: 5,
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'year',
          displayFormats: {
            year: 'yyyy',
          },
          tooltipFormat: 'MMM yyyy',
        },
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
          font: {
            size: 10,
          },
          color: '#64748b',
        },
      },
      y: {
        beginAtZero: false,
        min: minPrice,
        max: maxPrice,
        title: {
          display: true,
          text: 'Price (£)',
          font: {
            size: 12,
            weight: 500,
          },
        },
        ticks: {
          callback: (value) => `£${Math.round(value / 1000)}k`,
          count: 6,
          font: {
            size: 10,
          },
          color: '#64748b',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  };

  // Calculate 6-year averages and increases for each property type
  const calculateStats = () => {
    if (!data.series[0].data) return null;

    // Group data by property type
    const statsByType = {};

    data.series.forEach((series) => {
      if (!series.data || series.data.length === 0) return;

      // Get the property type using our mapping for consistency
      let propertyType = 'Other';

      if (series.data && series.data.length > 0 && series.data[0].propertyType) {
        // If the data point has a propertyType property, use it
        const rawType = series.data[0].propertyType;
        propertyType =
          propertyTypeMap[rawType] || rawType.charAt(0).toUpperCase() + rawType.slice(1);
      } else if (series.name) {
        // Fall back to the series name
        propertyType = propertyTypeMap[series.name] || series.name;
      }

      if (!statsByType[propertyType]) {
        statsByType[propertyType] = {
          points: [],
          prices: [],
          years: new Set(),
          icon: propertyIcons[propertyType] || FaHome,
          color: colors[propertyType] || '#3498DB',
        };
      }

      // Add data points to the property type group
      series.data.forEach((point) => {
        statsByType[propertyType].points.push(point);
        statsByType[propertyType].prices.push(point.y);
        statsByType[propertyType].years.add(new Date(point.x).getFullYear());
      });
    });

    // Calculate stats for each property type
    const stats = Object.keys(statsByType).map((propertyType) => {
      const { points, prices, years, icon, color } = statsByType[propertyType];

      // Calculate average price
      const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;

      // Calculate price increase
      const firstPrice = Math.min(...prices);
      const lastPrice = Math.max(...prices);
      const increase = ((lastPrice - firstPrice) / firstPrice) * 100;

      // Calculate annual increase rate
      const yearsDiff = years.size > 1 ? Math.max(...years) - Math.min(...years) : 1; // Default to 1 to avoid division by zero

      const annualIncrease =
        yearsDiff > 0 ? (Math.pow(1 + increase / 100, 1 / yearsDiff) - 1) * 100 : 0;

      // Calculate max price
      const maxPrice = Math.max(...prices);

      // Calculate last price
      const lastPricePoint =
        points.length > 0 ? points.reduce((a, b) => (a.x > b.x ? a : b)) : { y: 0 };
      const lastSalePrice = lastPricePoint.y;

      // Calculate estimated current price based on trend line
      let estimatedCurrentPrice = lastSalePrice; // Default to last sale price

      if (points.length >= 2) {
        // Calculate trend line
        const trendLine = calculateTrendline(points);

        if (trendLine) {
          // Get the slope and intercept from the trend line
          const x1 = trendLine[0].x;
          const y1 = trendLine[0].y;
          const x2 = trendLine[1].x;
          const y2 = trendLine[1].y;

          const slope = (y2 - y1) / (x2 - x1);
          const intercept = y1 - slope * x1;

          // Calculate the current price using today's date
          const currentDate = new Date().getTime();
          estimatedCurrentPrice = slope * currentDate + intercept;

          // Ensure the estimated price is positive
          estimatedCurrentPrice = Math.max(estimatedCurrentPrice, 0);
        }
      }

      return {
        label: propertyType,
        avgPrice,
        totalIncrease: increase,
        annualIncrease,
        maxPrice,
        lastPrice: lastSalePrice,
        estimatedCurrentPrice, // Add estimated current price
        icon,
        color,
        points, // Add points to the returned object
      };
    });

    return stats;
  };

  const stats = calculateStats();

  // Get property address or area label and normalize flat prefixes
  let title;
  if (isSingleProperty) {
    // Handle property address with potential duplicate "Flat" prefix
    if (data.propertyAddress) {
      // Normalize flat prefixes - remove duplicate "Flat" or "FLAT"
      if (
        data.propertyAddress.toLowerCase().startsWith('flat flat') ||
        data.propertyAddress.toLowerCase().startsWith('flat, flat')
      ) {
        // Remove the first occurrence of "Flat" or "FLAT"
        title = data.propertyAddress.replace(/^(flat|FLAT)\s*,?\s*/i, '');
      } else {
        title = data.propertyAddress;
      }
    } else {
      title = 'Property';
    }
  } else {
    title = data.areaName || 'Property Types in Area';
  }

  // Get postcode for the title if available
  const postcode = data.postcode || '';

  return (
    <Box>
      <style>{keyframes}</style>
      <Box
        p={4}
        bg='#EBF8FF'
        borderRadius='lg'
        borderWidth='1px'
        borderColor='gray.200'
        boxShadow='sm'
      >
        {/* Title */}
        <Flex justifyContent='space-between' alignItems='center' mb={4} pl={2}>
          <Text fontSize='lg' fontWeight='bold' color='#2B6CB0'>
            {isSingleProperty ? `Price History for ${title}` : `Area Price Trends for ${postcode}`}
          </Text>
          <Badge colorScheme='green' fontSize='xs'>
            {postcode}
          </Badge>
        </Flex>

        {/* Main chart content continues below */}

        {/* Main Chart */}
        <Box height='320px'>
          <Line
            data={{
              datasets: chartData.datasets,
            }}
            options={options}
          />
        </Box>

        {/* Price Analysis */}
        <Box mt={6} pt={4} borderTop='1px' borderColor='gray.100'>
          <Text fontSize='md' fontWeight='semibold' mb={3}>
            Price Analysis
          </Text>

          <SimpleGrid columns={1} spacing={4}>
            {stats.length === 0 ? (
              <Box p={3} bg='gray.50' borderRadius='md' border='1px' borderColor='gray.100'>
                <Text fontSize='sm' color='gray.600' fontStyle='italic'>
                  No price data available for this property type in this area.
                </Text>
              </Box>
            ) : (
              stats.map((stat, index) => (
              <Box
                key={`stat-${stat.label}-${index}`}
                p={3}
                bg='gray.50'
                borderRadius='md'
                border='1px'
                borderColor='gray.100'
              >
                <Text fontWeight='medium' color='gray.700'>
                  {stat.label !== 'Unknown' ? stat.label : (data.currentPropertyType ? data.currentPropertyType.charAt(0).toUpperCase() + data.currentPropertyType.slice(1) : 'Property')}
                </Text>

                <Stack spacing={1} mt={2}>
                  {/* Check if there's only one data point */}
                  {stat.points && stat.points.length === 1 ? (
                    <HStack justify='space-between'>
                      <Text fontSize='sm' color='gray.600'>
                        {stat.lastPrice === 0 ? 'Previous Sales:' : 'Last Sale Price:'}
                      </Text>
                      <Text fontSize='sm' fontWeight='medium'>
                        {stat.lastPrice === 0 
                          ? 'No previous sales found for this property'
                          : `£${stat.lastPrice.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}`
                        }
                      </Text>
                    </HStack>
                  ) : (
                    <>
                      {stat.estimatedCurrentPrice > 0 && (
                        <HStack justify='space-between'>
                          <Text fontSize='sm' color='gray.600' fontWeight='semibold'>
                            Estimated Current Price:
                          </Text>
                          <Text fontSize='sm' fontWeight='bold' color='blue.600'>
                            £
                            {stat.estimatedCurrentPrice.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </Text>
                        </HStack>
                      )}

                      {stat.avgPrice > 0 && (
                        <HStack justify='space-between'>
                          <Text fontSize='sm' color='gray.600'>
                            6-Year Average:
                          </Text>
                          <Text fontSize='sm' fontWeight='medium'>
                            £
                            {stat.avgPrice.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </Text>
                        </HStack>
                      )}

                      {!isNaN(stat.totalIncrease) && stat.points && stat.points.filter(p => p.y > 0).length >= 2 && (
                        <HStack justify='space-between'>
                          <Text fontSize='sm' color='gray.600'>
                            Total Increase:
                          </Text>
                          <Text
                            fontSize='sm'
                            fontWeight='medium'
                            color={
                              stat.totalIncrease > 0
                                ? 'green.500'
                                : stat.totalIncrease < 0
                                ? 'red.500'
                                : 'gray.600'
                            }
                          >
                            {stat.totalIncrease > 0 ? '+' : ''}
                            {stat.totalIncrease.toFixed(1)}%
                          </Text>
                        </HStack>
                      )}

                      {!isNaN(stat.annualIncrease) && stat.points && stat.points.filter(p => p.y > 0).length >= 2 && (
                        <HStack justify='space-between'>
                          <Text fontSize='sm' color='gray.600'>
                            Annual Rate:
                          </Text>
                          <Text
                            fontSize='sm'
                            fontWeight='medium'
                            color={
                              stat.annualIncrease > 0
                                ? 'green.500'
                                : stat.annualIncrease < 0
                                ? 'red.500'
                                : 'gray.600'
                            }
                          >
                            {stat.annualIncrease > 0 ? '+' : ''}
                            {stat.annualIncrease.toFixed(1)}%
                          </Text>
                        </HStack>
                      )}

                      {stat.maxPrice > 0 && (
                        <HStack justify='space-between'>
                          <Text fontSize='sm' color='gray.600'>
                            Max Price:
                          </Text>
                          <Text fontSize='sm' fontWeight='medium'>
                            £
                            {stat.maxPrice.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </Text>
                        </HStack>
                      )}

                      {stat.lastPrice > 0 && (
                        <HStack justify='space-between'>
                          <Text fontSize='sm' color='gray.600'>
                            Last Price:
                          </Text>
                          <Text fontSize='sm' fontWeight='medium'>
                            £
                            {stat.lastPrice.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </Text>
                        </HStack>
                      )}
                      
                      {/* Show a message if no valid price points are available */}
                      {(!stat.points || stat.points.filter(p => p.y > 0).length === 0) && (
                        <Text fontSize='sm' color='gray.600' fontStyle='italic'>
                          No price data available for {stat.label !== 'Unknown' ? `this ${stat.label.toLowerCase()}` : (data.currentPropertyType ? `this ${data.currentPropertyType}` : 'this property type')}.
                        </Text>
                      )}
                    </>
                  )}
                </Stack>
              </Box>
            )))
            }
          </SimpleGrid>
        </Box>

        {/* Stats Section (enhanced) */}
        {/* <Box mt={6}>
          <StatGroup>
            {overallTrend !== null && (
              <Stat>
                <StatLabel>Price Change</StatLabel>
                <StatNumber color={overallTrend > 0 ? "green.500" : "red.500"}>
                  {Math.abs(overallTrend).toFixed(1)}%
                </StatNumber>
                <StatHelpText>
                  <StatArrow type={overallTrend > 0 ? 'increase' : 'decrease'} />
                  {trendAnalysis?.firstYear} - {trendAnalysis?.lastYear}
                </StatHelpText>
              </Stat>
            )}
            
            {annualizedRate !== null && (
              <Stat>
                <StatLabel>
                  <HStack>
                    <Text>Annual Growth</Text>
                    <ChakraTooltip label="Compound Annual Growth Rate" fontSize="xs">
                      <span><Icon as={FaInfoCircle} color="gray.400" /></span>
                    </ChakraTooltip>
                  </HStack>
                </StatLabel>
                <StatNumber color={annualizedRate > 0 ? "green.500" : "red.500"}>
                  {Math.abs(annualizedRate).toFixed(1)}%
                </StatNumber>
                <StatHelpText>
                  <StatArrow type={annualizedRate > 0 ? 'increase' : 'decrease'} />
                  Per year
                </StatHelpText>
              </Stat>
            )}
            
            {data.averagePrice && (
              <Stat>
                <StatLabel>Average Price</StatLabel>
                <StatNumber>£{data.averagePrice.toLocaleString()}</StatNumber>
                <StatHelpText>
                  Based on {data.series[0].data.length} sales
                </StatHelpText>
              </Stat>
            )}
          </StatGroup>
        </Box> */}
      </Box>
    </Box>
  );
}

export default PriceHistoryChart;
